import { Spinner, Card } from "react-bootstrap";
import { useMemo, useEffect, useRef } from "react";
import { useTranslation } from 'react-i18next';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { SelectedFilter } from "../../../../components/Filters/types";
import { STYLE, COLOR } from "../../../../config/constraits";
import { saveBeforeFilter, addFilter } from "../../../../redux/filtersSlice";
import { generateCsvFile } from "../../../../utils/downloadCsvFile";
import { formatMonthYear } from "../../../../utils/formatters/DateFormatter";
import type { ClaimStatusLineChartShow } from "../../types";
import { ClaimStatusEnum } from "../../types/ClaimStatusEnum";
import ChartShareActions from "../../../../components/ChartShareActions";
import { RootState } from "../../../../redux/store";
import { useClaimStatusLineChartData } from "../../hooks/useClaimStatusLineChartData";
import { AuditActionEnum } from "../../../auditLogs/types";
interface ClaimStatusLineChartProps {
    setMessage: (msg: string) => void;
}
export default function ClaimStatusLineChart({ setMessage }: ClaimStatusLineChartProps) {
    const { t, i18n } = useTranslation('claim');
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const feelingchartRef = useRef<HTMLDivElement>(null);
    const selectedFilters = useSelector((state: RootState) => state.filters.claims.selectedFilters);

    const { data, isLoading, error } = useClaimStatusLineChartData();

    useEffect(() => {
        if (error) {
            const messageError = (error as any)?.response?.data?.message || (error as Error).message;
            setMessage(messageError);
        }
    }, [error, setMessage]);

    const { chartData, maxY } = useMemo(() => {
        if (!data) {
            return { chartData: [], maxY: 0 };
        }

        let max = 0;

        const processedData: ClaimStatusLineChartShow[] = data.map((item) => {
            const countOpen = item.countOpen ?? 0;
            const countClosed = item.countClosed ?? 0;

            if (countOpen > max || countClosed > max) {
                max = countOpen > countClosed ? countOpen : countClosed;
            }

            return {
                date: capitalizeFirstLetter(
                    new Date(item.date).toLocaleDateString(i18n.language, { month: "long" })
                ),
                countOpen,
                countClosed
            };
        });

        return { chartData: processedData, maxY: max };
    }, [data, i18n.language]);

    const handlePointClick = (payload: ClaimStatusLineChartShow, status: number | null) => {
        dispatch(saveBeforeFilter('claims'));
        if (status != null) {
            const statusFilter: SelectedFilter = {
                name: 'Status',
                description: 'Status',
                values: [status.toString()]
            };
            dispatch(addFilter({ table: 'claims', filter: statusFilter }));
        }

        const itemData = data?.find(item => capitalizeFirstLetter(new Date(item.date).toLocaleDateString(i18n.language, { month: "long" })) == payload.date);
        const dateFilter: SelectedFilter = {
            name: status == ClaimStatusEnum.Closed ? 'ClosingDate' : 'DataCreacio',
            description: status == ClaimStatusEnum.Closed ? 'ClosingDate' : 'DataCreacio',
            dateValues: { initialDate: itemData?.initialDate, finalDate: itemData?.finalDate }
        };
        dispatch(addFilter({ table: 'claims', filter: dateFilter }));
        navigate('/claims/historic');
    };

    function capitalizeFirstLetter(text: string): string {
        if (!text) return "";
        return text.charAt(0).toUpperCase() + text.slice(1);
    }

    const exportLineChartCsvData = async (): Promise<void> => {
        const rows = (data ?? []) as ClaimStatusLineChartShow[];
        await generateCsvFile({
            headers: [
                t("claim:statusLineChart.export.headerMonthYear"),
                t("claim:statusLineChart.labelOpen"),
                t("claim:statusLineChart.labelClosed"),
            ],
            rows,
            mapRow: (item) => [
                formatMonthYear(item.date),
                item.countOpen ?? 0,
                item.countClosed ?? 0,
            ],
            fileBaseName: "claims_status_timeline",
            trailingSemicolon: true,
        });
    };

    return (
        <>
            {isLoading && (
                <div className="overlay-spinner">
                    <Spinner animation="border" variant="light" />
                </div>
            )}
            <div ref={feelingchartRef}>
                <Card
                    style={{
                        backgroundColor: "#F5F5F5",
                        border: "none",
                        borderRadius: STYLE.BORDER_RADIUS,
                    }}
                    className="p-3"
                >
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h4>{t("claim:statusLineChart.labelTitle")}</h4>
                        <div className="d-flex align-items-center">
                            <ChartShareActions
                                chartRef={feelingchartRef}
                                title={t("claim:statusLineChart.labelTitle")}
                                filters={selectedFilters}
                                t={t}
                                exportCsvAction={exportLineChartCsvData}
                                exportCsvAudit={{
                                    entity: "DashboardIndicator",
                                    action: AuditActionEnum.ExportCsv,
                                    source: window.location.pathname,
                                    description: "CSV export of claim status timeline",
                                    filters: selectedFilters
                                }}
                            />
                        </div>
                    </div>
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis
                                dataKey="date"
                                angle={-65}
                                textAnchor="end"
                                height={80}
                            />
                            <YAxis type="number" domain={[0, maxY || 10]} />
                            <Tooltip />
                            <Legend verticalAlign="bottom" align="center" height={36} />
                            <Line
                                type="monotone"
                                dataKey="countOpen"
                                name={t("claim:statusLineChart.labelOpen")}
                                stroke={COLOR.PURPLE}
                                dot={{ r: 0 }}
                                activeDot={{ r: 5, onClick: (_, payload: any) => handlePointClick(payload.payload, null) }}
                            />
                            <Line
                                type="monotone"
                                dataKey="countClosed"
                                name={t("claim:statusLineChart.labelClosed")}
                                stroke={COLOR.VERD}
                                dot={{ r: 0 }}
                                activeDot={{ r: 5, onClick: (_, payload: any) => handlePointClick(payload.payload, ClaimStatusEnum.Closed) }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </Card>
            </div>
        </>
    );
}

